import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { allArticles } from "../data/articles";
import { useGSAPReveal } from "../util/useGSAPReveal";

type Article = {
  title: string;
  thumbnail: string;
  link: string;
  date?: string;
  platform?: string;
  publishedIn?: string;
  tags?: string[];
};

const ArticlesSection: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isMobileView, setIsMobileView] = useState(false);

  // GSAP scroll-triggered animations
  const headerRef = useGSAPReveal({ variant: 'fade-up' });
  const listRef = useGSAPReveal({ variant: 'stagger-children', delay: 0.1, staggerAmount: 0.15 });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 900);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    try {
      setArticles(allArticles || []);
    } catch (error) {
      console.error('Error loading articles:', error);
      setArticles([]);
    }
  }, []);

  const displayArticles = articles.slice(0, 3);

  return (
    <section
      style={{
        minHeight: '80vh',
        background: 'transparent',
        padding: isMobileView ? '80px 0 100px 0' : '120px 0 140px 0',
        fontFamily: "'Inter', sans-serif",
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div style={{ width: '100%', position: 'relative', zIndex: 2 }}>
        {/* Header Container */}
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: isMobileView ? '0 24px' : '0 80px' }}>
          <div
            ref={headerRef}
            style={{
              position: 'relative',
              marginBottom: isMobileView ? '48px' : '64px',
              display: 'flex',
              flexDirection: isMobileView ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobileView ? 'flex-start' : 'flex-end',
              gap: '24px'
            }}
          >
            <div>
              {/* Copper accent line */}
              <div
                style={{
                  width: '40px',
                  height: '2px',
                  background: '#C4956A',
                  marginBottom: '24px'
                }}
              />
              <h2
                style={{
                  fontSize: isMobileView ? '40px' : '56px',
                  fontWeight: 600,
                  color: '#F5F5F5',
                  fontFamily: "'Space Grotesk', sans-serif",
                  marginBottom: '16px',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1
                }}
              >
                Writings
              </h2>
            </div>

            {/* View All Button (Desktop aligns right with header) */}
            {!isMobileView && (
              <Link
                to="/articles"
                className="view-all-link"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#C4956A',
                  textDecoration: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                  transition: 'color 0.3s ease',
                  paddingBottom: '8px'
                }}
              >
                <span>View all articles</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="arrow-icon"
                  style={{ transition: 'transform 0.3s ease' }}
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>

          {/* Minimalist List Container */}
          <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid rgba(245, 245, 245, 0.06)' }}>
            {displayArticles.length > 0 ? (
              displayArticles.map((article, index) => (
                <a
                  key={index}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="article-row"
                  style={{
                    display: 'flex',
                    flexDirection: isMobileView ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobileView ? 'flex-start' : 'center',
                    padding: isMobileView ? '32px 0' : '40px 24px',
                    borderBottom: '1px solid rgba(245, 245, 245, 0.06)',
                    textDecoration: 'none',
                    gap: isMobileView ? '16px' : '32px',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    borderRadius: '8px',
                    margin: isMobileView ? '0' : '0 -24px',
                  }}
                >
                  {/* Left Side: Title & Tags */}
                  <div style={{ flex: 1, maxWidth: isMobileView ? '100%' : '75%' }}>
                    <h3
                      className="article-title"
                      style={{
                        fontSize: isMobileView ? '22px' : '26px',
                        fontWeight: 500,
                        color: '#E5E5E5',
                        fontFamily: "'Space Grotesk', sans-serif",
                        lineHeight: 1.3,
                        marginBottom: '12px',
                        letterSpacing: '-0.01em',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {article.title}
                    </h3>

                    {article.tags && article.tags.length > 0 && (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {article.tags.slice(0, 3).map((tag, tIndex) => (
                          <span key={tIndex} style={{
                            fontSize: '12px',
                            color: '#737373',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}>
                            {tag}{tIndex < Math.min(article.tags!.length, 3) - 1 ? <span style={{ color: '#404040', margin: '0 8px' }}>•</span> : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right Side: Meta & Arrow */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobileView ? '24px' : '40px',
                    minWidth: isMobileView ? '100%' : '180px',
                    justifyContent: isMobileView ? 'space-between' : 'flex-end',
                    marginTop: isMobileView ? '8px' : '0'
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isMobileView ? 'flex-start' : 'flex-end',
                      gap: '4px'
                    }}>
                      {article.platform && (
                        <span style={{
                          fontSize: '13px',
                          color: '#A3A3A3',
                          fontWeight: 500,
                        }}>
                          {article.platform}
                        </span>
                      )}
                      {article.date && (
                        <span style={{
                          fontSize: '12px',
                          color: '#525252',
                          fontFamily: "'JetBrains Mono', monospace",
                          letterSpacing: '0.05em'
                        }}>
                          {article.date}
                        </span>
                      )}
                    </div>

                    <div
                      className="article-arrow"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '1px solid rgba(196, 149, 106, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#C4956A',
                        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        background: 'transparent'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"></line>
                        <polyline points="7 7 17 7 17 17"></polyline>
                      </svg>
                    </div>
                  </div>
                </a>
              ))
            ) : (
              <div style={{ padding: '60px 0', textAlign: 'center', color: '#737373' }}>
                Loading articles...
              </div>
            )}
          </div>

          {/* Mobile View All Button */}
          {isMobileView && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '48px' }}>
              <Link
                to="/articles"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#C4956A',
                  textDecoration: 'none',
                  fontSize: '15px',
                  fontWeight: 500,
                  borderBottom: '1px solid rgba(196, 149, 106, 0.3)',
                  paddingBottom: '4px'
                }}
              >
                <span>View all articles</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div >

      <style>{`
        .article-row:hover {
          background: rgba(255, 255, 255, 0.02);
        }
        
        @media (hover: hover) {
          .article-row:hover .article-title {
            color: #C4956A !important;
          }
          
          .article-row:hover .article-arrow {
            background: #C4956A !important;
            color: #111111 !important;
            border-color: #C4956A !important;
            transform: translateX(4px);
          }
        }

        .view-all-link:hover {
          color: #D4A87A !important;
        }
        
        .view-all-link:hover .arrow-icon {
          transform: translateX(4px);
        }
      `}</style>
    </section >
  );
};

export default ArticlesSection;
